import {
  ApolloLink,
  createHttpLink,
  type FetchResult,
  Observable,
  type Observer,
  type Operation,
} from '@apollo/client';
import {onError} from '@apollo/client/link/error';
import {RetryLink} from '@apollo/client/link/retry';
import {getCookie} from '@wandb/weave/common/util/cookie';
import * as queryString from 'query-string';
import {type Dispatch, type Store} from 'redux';

import {errorInjectionLink} from '../apolloErrorInjection';
import {backendHost} from '../config';
import {captureError, ErrorLevel} from '../integrations';
import {
  displayError,
  displayErrorPortal,
  resetErrorPortal,
} from '../state/global/actions';
import {
  isQueryOperation,
  recordGqlSample,
} from '../state/serviceHealth/tracker';
import {type RootState} from '../types/redux';
import {compressSpecInWorker} from '../workers/compressViewSpec.client';
import {getServerParseErrorContext} from './apolloErrorTelemetry';
import {createLatestWinsLink} from './apolloLatestWins';
import {
  get503RetryDelayMs,
  getRetryAfterMs,
  hasShedRetryAfterExtension,
  is503Error,
  MAX_ATTEMPTS_503,
  parseRetryAfterHeaderMs,
  RETRY_AFTER_HOLDER_CONTEXT_KEY,
  RETRY_AFTER_SECONDS_EXTENSION,
  type RetryAfterSecondsHolder,
} from './apolloRetryAfter';
import {
  REQUEST_CAUSE_HEADER,
  RETRY_ATTEMPT_HEADER,
  stampRetryHeader,
} from './apolloRetryHeader';
import {isForceTraceActive} from './forceTrace';
import {getPerfTimingLink} from './getPerfTimingLink';
import {getWandbTraceId} from './getWandbTraceId';
import {isOnReportView} from './urls';

// These global vars are set by `createCircularDependencies` below :P
// There should definitely be a better way of doing this.
let dispatch: Dispatch;
// auth is from auth.js and is currently untyped
let auth: any = null;
let reduxStore: Store<RootState> | null = null;

export const apolloEndpoint = `${backendHost()}/graphql`;

const httpLink = createHttpLink({
  fetch: (input: URL | RequestInfo, init?: RequestInit): Promise<Response> => {
    // we force using window.fetch because we need to use datadog's updated instance of window.fetch
    return window.fetch(input, init);
  },
  uri: apolloEndpoint,
  // Our credentials may be a cookie on a different domain to backend
  // `api.wandb.ai` vs `wandb.ai`
  // https://www.apollographql.com/docs/react/networking/authentication/#cookie
  credentials: 'include',
});

// Adds a header specifying that an admin wants to use their
// admin privileges based on a cookie that they
// can manually toggle.
const useAdminPrivilegesMiddleware = new ApolloLink((operation, forward) => {
  const useAdminPrivileges = getCookie('use_admin_privileges') === 'true';
  if (useAdminPrivileges) {
    setHeader(operation, 'use-admin-privileges', 'true');
  }
  return forward!(operation);
});

const useViewAsMiddleware = new ApolloLink((operation, forward) => {
  const useViewAs = getCookie('impersonated_username');
  if (useViewAs) {
    setHeader(operation, 'impersonated-username', useViewAs);
  }
  return forward!(operation);
});

const forceTraceMiddleware = new ApolloLink((operation, forward) => {
  const viewerEmail = reduxStore?.getState().viewer.viewer?.email;
  if (isForceTraceActive(viewerEmail)) {
    setHeader(operation, 'x-wandb-force-trace', 'true');
  }
  return forward!(operation);
});

// This middleware currently compresses the spec field on UpsertView2 only.
// Once https://github.com/wandb/core/pull/19264/files is merged, we can modify this middleware
// to compress the entire request body for better performance.
const compressionMiddleware = new ApolloLink((operation, forward) => {
  const safeForward = forward!;
  return new Observable<FetchResult>((observer: Observer<FetchResult>) => {
    if (operation.operationName === 'UpsertView2') {
      const {spec, specIsGzipped} = operation.variables;
      if (spec && !specIsGzipped) {
        compressSpecInWorker(spec)
          .then(compressed => {
            operation.variables.spec = compressed;
            operation.variables.specIsGzipped = true;
            safeForward(operation).subscribe(observer);
          })
          .catch(e => {
            console.error('Error compressing view spec', e);
            safeForward(operation).subscribe(observer);
          });
      } else {
        safeForward(operation).subscribe(observer);
      }
    } else {
      safeForward(operation).subscribe(observer);
    }
  });
});

const authMiddleware = new ApolloLink((operation, forward) => {
  const safeForward = forward!;
  return new Observable<FetchResult>((observer: Observer<FetchResult>) => {
    auth.ensureCurrentIdToken().then((t: string | null) => {
      // The signup flow accepts a token
      const qs = queryString.parse(window.location.search);
      const token = qs.token || t;
      const accessToken = qs.accessToken;

      setHeader(operation, 'X-Origin', window.location.origin);

      if (token) {
        setHeader(operation, 'authorization', `Bearer ${token}`);
      }

      if (accessToken && isOnReportView()) {
        setHeader(operation, 'access-token', accessToken as string);
      }
      if (isOnReportView()) {
        setHeader(operation, 'report-view', 'true');
      }

      // [Deprecated] Anonymous API keys are disabled, but old links may still
      // contain ?apiKey=... in the URL. Scrub it for safety, even though we can't use that key.
      if (qs.apiKey) {
        const newQueryString = {...qs};
        delete newQueryString.apiKey;
        window.location.search = queryString.stringify(newQueryString);
      }

      safeForward(operation).subscribe(observer);
    });
  });
});

const errorLink = onError(
  ({operation, response, networkError, graphQLErrors}) => {
    // NOTE: Don't accidentally return something from this function! You'll
    // potentially get an error that says "retriedResult.subscribe is not a
    // function" because you're not returning an observable.

    console.log('Apollo NetworkErrors:', networkError);
    console.log('Apollo GraphQLErrors:', graphQLErrors);

    const operationContext = operation.getContext();
    const traceId = getWandbTraceId(networkError, operation);
    const errorTelemetryContext = {
      operationName: operation.operationName,
      propagateErrors: Boolean(operationContext.propagateErrors),
      requestCause: operationContext.headers?.[REQUEST_CAUSE_HEADER],
      retryAttempt: operationContext.headers?.[RETRY_ATTEMPT_HEADER],
      traceId,
    };
    const serverParseErrorContext = getServerParseErrorContext(networkError);
    if (serverParseErrorContext != null) {
      captureError(networkError, 'apollo_server_parse_error', {
        level: ErrorLevel.Info,
        extra: {
          ...errorTelemetryContext,
          ...serverParseErrorContext,
        },
      });
    }

    // Regardless of propagateErrors, we need to bubble up 401's so the frontend
    // can prompt us to login.
    if (
      // eslint-disable-next-line no-extra-boolean-cast
      !Boolean(operation.getContext().noLogin) &&
      networkError &&
      (networkError as any).statusCode === 401
    ) {
      dispatch(displayErrorPortal('Session expired. Forcing login...'));
      dispatch(
        displayError({
          code: 401,
          message: networkError.message,
          traceId,
        })
      );
      return;
    }

    // When propagateErrors is set on the context, the query issuer wants to
    // handle errors manually so abort processing.
    // eslint-disable-next-line no-extra-boolean-cast
    if (Boolean(operation.getContext().propagateErrors)) {
      return;
    }

    if (graphQLErrors) {
      // Capture GraphQL errors
      graphQLErrors.forEach(({message, locations, path}) => {
        captureError(`[GraphQL Error] ${message}`, 'apollo_graphql_error', {
          level: ErrorLevel.Info,
          extra: {
            operation: operation.operationName,
            path,
            locations,
          },
        });
      });
      if (graphQLErrors.length > 0) {
        // Just show the last error if we have multiple graphQL errors.
        const lastError = graphQLErrors[graphQLErrors.length - 1];
        dispatch(displayError({...lastError, ...(traceId ? {traceId} : {})}));
      }
      return;
    }

    if (networkError) {
      if (networkError.message === 'Failed to fetch') {
        dispatch(displayErrorPortal('Offline. Trying to reconnect...'));
        return;
      }

      captureError(
        `[Network Error] ${networkError.message}`,
        'apollo_network_error',
        {
          level: ErrorLevel.Info,
          extra: {
            operation: operation.operationName,
            error: networkError,
            response,
          },
        }
      );

      const {result, errResult} = networkError as any;
      const errorMessage =
        (errResult && errResult.error) ||
        (result && result.error) ||
        'Application Error';
      dispatch(
        displayError({
          code: (networkError as any).statusCode || 503,
          message: errorMessage,
          traceId,
        })
      );
    }
  }
);

const resetErrorPortalLink = new ApolloLink((operation, forward) => {
  const observable = forward(operation);
  return new Observable<FetchResult>((observer: Observer<FetchResult>) => {
    observable.subscribe({
      complete: () => {
        // We delay resetting the error portal so the user can see the message
        setTimeout(() => dispatch(resetErrorPortal()), 3000);
        observer.complete?.();
      },
      error: (e: unknown) => observer.error?.(e),
      next: (n: FetchResult) => observer.next?.(n),
    });
  });
});

/**
 * The goal here is to create a structured way to do the following:
 * 1. Match against multiple operation names
 * 2. Invoke a custom delay function per match
 * 3. Invoke a custom retry function per match
 *
 * This allows the overriding of the retry logic for a matched group of operations. Aggressive retries end up hammering the API for certain 500 conditions. See Adrian's note below.
 */
type SpecialHandlers = 'BackendLinesDeltaQuery' | undefined;
const getSpecialHandler = (operationName: string): SpecialHandlers => {
  const isBackendLinesDeltaQuery = new RegExp('BackendLinesDeltaQuery').test(
    operationName
  );
  if (isBackendLinesDeltaQuery) {
    return 'BackendLinesDeltaQuery';
  }

  return undefined;
};
const attemptsByHandler: Record<
  Exclude<SpecialHandlers, undefined>,
  (attempt: number, operation: Operation) => boolean
> = {
  BackendLinesDeltaQuery: (attempt, operation) => {
    if (attempt === 1) {
      return true;
    }
    return false;
  },
};
const delaysByHandler: Record<
  Exclude<SpecialHandlers, undefined>,
  (count: number, operation: Operation) => number
> = {
  BackendLinesDeltaQuery: (count, operation) => {
    return 1000;
  },
};

// Note apollo is a POS and doesn't consistently return the error object.
// We check for the case where error.statusCode is not present, which typically
// means a request failed in a retryable way (for us).
// https://github.com/apollographql/apollo-link/issues/300
const shouldRetryOperation = (
  attempt: number,
  operation: Operation,
  error: any
): boolean => {
  // this replaces the `max` option in the `attempts` object
  if (attempt > 6) {
    return false;
  }
  if (operation.getContext().doNotRetry) {
    return false;
  }

  // A 503 is the backend shedding load on purpose (circuit breaker open /
  // bulkhead full): allow fewer tries, spaced by Retry-After (see delay).
  // Must win over special handlers — BLDQ's single 1s retry was for 500
  // storms, not intentional load-shed.
  if (is503Error(error)) {
    return attempt < MAX_ATTEMPTS_503;
  }

  const specialHandler = getSpecialHandler(operation.operationName);
  if (specialHandler) {
    return attemptsByHandler[specialHandler](attempt, operation);
  }

  /**
   * TODO(adrian): We should probably not be retrying 500s.
   *
   * It's safer for us to aggressively retry like this but it causes lots of spurious requests and makes a mess in the logs.
   */
  const is500Error = error && error.statusCode && error.statusCode >= 500;

  // Happens when the fetch API receives a rejected promise, which happens on temporary server failures sometimes.
  const isFetchError = error && error.statusCode == null;

  // The response body wasn't JSON, e.g. an HTML error page from a proxy.
  const isServerParseError = error?.name === 'ServerParseError';

  // Only retry internal errors
  return is500Error || isFetchError || isServerParseError;
};

const retryLink = new RetryLink({
  // converting this from the attempts object to the attempts function
  attempts: (attempt, operation, error) => {
    // Feed the degraded-service window per 503 response the server actually
    // returned — attempts is consulted on every failed attempt, so this
    // counts server-side shedding even while retries are still in flight.
    if (is503Error(error) && isQueryOperation(operation)) {
      recordGqlSample('unavailable');
    }

    const willRetry = shouldRetryOperation(attempt, operation, error);
    if (willRetry) {
      const retryAfterMs = getRetryAfterMs(error);
      stampRetryHeader(
        operation,
        attempt,
        is503Error(error) && retryAfterMs != null && retryAfterMs > 0
      );
    }
    return willRetry;
  },
  /**
   * The delay config object doesn't seem to do exponential backoff well, so we're implementing a custom function for it here. Count will range from 0 to `attempts.max`.
   */
  delay: (count, operation, error) => {
    // Same precedence as shouldRetryOperation: 503/Retry-After before BLDQ.
    if (is503Error(error)) {
      return get503RetryDelayMs(error);
    }

    const specialHandler = getSpecialHandler(operation.operationName);
    if (specialHandler) {
      return delaysByHandler[specialHandler](count, operation);
    }

    const sign = Math.random() < 0.5 ? -1 : 1;
    const random = sign * Math.ceil(Math.random() * 75);

    // we add a little bit of randomness to the delay so all the panels aren't retrying at exactly the same time
    const delay = 200 * Math.pow(2, count) + random;
    return delay;
  },
});

// Feeds the degraded-service window with terminal query outcomes:
// - 'unavailable': HTTP 503 (counted in retryLink) OR HTTP 200 + Retry-After
//   (partial shed → incomplete runs; extension set by retryAfterExtensionsLink)
// - 'ok': plain success (including progressive-load incompletes with no shed)
// - 'error': terminal non-503 failure
// Terminal 503s are skipped here — already counted per-response in retryLink.
// Sits above retryLink so it sees final outcomes; Retry-After extensions are
// still present because retryAfterExtensionsLink runs below and maps upward.
const serviceHealthLink = new ApolloLink((operation, forward) => {
  const safeForward = forward!;
  if (!isQueryOperation(operation)) {
    return safeForward(operation);
  }
  return new Observable(observer => {
    const sub = safeForward(operation).subscribe({
      next: result => {
        recordGqlSample(
          hasShedRetryAfterExtension(result) ? 'unavailable' : 'ok'
        );
        observer.next(result);
      },
      error: e => {
        if (!is503Error(e)) {
          recordGqlSample('error');
        }
        observer.error(e);
      },
      complete: () => observer.complete(),
    });
    return () => sub.unsubscribe();
  });
});

// Copy Retry-After from the fetch Response onto (1) a caller-supplied context
// holder — ApolloQueryResult drops extensions, so this is how client.query()
// callers (incomplete-refetch) see the hint — and (2) FetchResult.extensions
// for any Observable consumer that still sees the full link result.
const retryAfterExtensionsLink = new ApolloLink((operation, forward) => {
  return forward(operation).map(result => {
    const headers = operation.getContext().response?.headers;
    if (headers == null || typeof headers.get !== 'function') {
      return result;
    }
    const ms = parseRetryAfterHeaderMs(headers.get('retry-after'));
    if (ms == null) {
      return result;
    }
    const seconds = Math.floor(ms / 1000);
    const holder = operation.getContext()[RETRY_AFTER_HOLDER_CONTEXT_KEY] as
      | RetryAfterSecondsHolder
      | undefined;
    if (holder != null) {
      holder.seconds = seconds;
    }
    return {
      ...result,
      extensions: {
        ...result.extensions,
        [RETRY_AFTER_SECONDS_EXTENSION]: seconds,
      },
    };
  });
});

const latestWinsLink = createLatestWinsLink();

export const apolloLink = ApolloLink.from([
  useAdminPrivilegesMiddleware,
  useViewAsMiddleware,
  forceTraceMiddleware,
  authMiddleware,
  errorInjectionLink, // Added error injection link (no-ops if ramp flag is disabled)
  errorLink,
  resetErrorPortalLink,
  serviceHealthLink,
  // This must wrap RetryLink so retries stay within their original request
  // lane and cannot supersede a newer operation from the same hook.
  latestWinsLink,
  retryLink,
  getPerfTimingLink(),
  compressionMiddleware,
  retryAfterExtensionsLink,
  httpLink,
]);

export const createCircularDependencies = (
  store: Store<RootState>,
  authIn: any
) => {
  dispatch = store.dispatch;
  reduxStore = store;
  auth = authIn;
};

interface OperationContext {
  headers?: {[key: string]: string};
}

function setHeader(op: Operation, key: string, value: string) {
  op.setContext(({headers = {}}: OperationContext) => ({
    headers: {
      ...headers,
      [key]: value,
    },
  }));
}
